import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminStore, type AdminUser } from '../../store/adminStore'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: object) => void
          renderButton: (el: HTMLElement, cfg: object) => void
          prompt: () => void
        }
      }
    }
  }
}

export default function AdminLoginPage() {
  const navigate    = useNavigate()
  const { setSession, isSessionValid } = useAdminStore()
  const btnRef      = useRef<HTMLDivElement>(null)
  const [err, setErr] = useState('')

  // Already logged in with a valid (non-expired) token → redirect
  useEffect(() => {
    if (isSessionValid()) navigate('/admin/overview', { replace: true })
  }, [isSessionValid, navigate])

  const handleCredential = useCallback(async (response: { credential: string }) => {
    setErr('')
    try {
      const res = await fetch('/api/v1/admin/auth/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential }),
      })
      if (!res.ok) {
        const data = await res.json()
        setErr(data.detail ?? 'Prijava nije uspela.')
        return
      }
      const data = await res.json()
      const user: AdminUser = {
        id:          data.user.id,
        email:       data.user.email,
        fullName:    data.user.full_name,
        picture:     data.user.picture,
        isoftRoles:  data.user.isoft_roles,
      }
      setSession(data.token, user)
      navigate('/admin/overview', { replace: true })
    } catch {
      setErr('Greška pri prijavi. Pokušajte ponovo.')
    }
  }, [setSession, navigate])

  useEffect(() => {
    const init = () => {
      if (!window.google || !btnRef.current) return
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredential,
        auto_select: false,
      })
      window.google.accounts.id.renderButton(btnRef.current, {
        theme: 'filled_black',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width: 280,
      })
    }

    if (window.google) {
      init()
    } else {
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.onload = init
      document.head.appendChild(script)
    }
  }, [handleCredential])

  return (
    <div className="flex items-center justify-center min-h-screen p-4" style={{ backgroundColor: '#0A1930' }}>
      <div className="bg-white rounded-2xl shadow-2xl p-10 w-full max-w-sm text-center space-y-8">
        {/* Logo */}
        <div className="space-y-2">
          <div className="w-14 h-14 bg-brand-cyan rounded-2xl flex items-center justify-center mx-auto shadow-lg">
            <svg className="w-7 h-7 text-brand-midnight" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">I-SOFT Admin</h1>
            <p className="text-sm text-gray-500 mt-0.5">Prijavite se Google nalogom</p>
          </div>
        </div>

        {/* Google button */}
        <div className="flex flex-col items-center gap-3">
          <div ref={btnRef} />
          {err && (
            <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-800">{err}</p>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400">
          Pristup je ograničen na I-SOFT tim.<br />
          Kontaktirajte super_admin za dodavanje naloga.
        </p>
      </div>
    </div>
  )
}
